import { createTheme, alpha, lighten, darken } from '@material-ui/core/styles';

import { getDefaultClassOverrides } from 'themes/getTheme/defaultTheme';

// using createTheme here will populate other dynamic color values
// then we can use some of those dynamic values elsewhere in this file
const { palette: quantumPalette } = createTheme({
  palette: {
    contrastThreshold: 4.5, // AA a11y standards for text contrast ratio
    tonalOffset: 0.3, // AA a11y standards for focus contrast ratio (buttons)

    primary: {
      main: '#5a8200',
    },
    secondary: {
      main: '#222222',
      dark: lighten('#222222', 0.1), // setting this to a lighter version so some contrast exist between .main and .dark.
      light: lighten('#222222', 0.3),
    },
    info: {
      main: '#0055b3',
    },
    success: {
      main: '#00875a',
    },
    warning: {
      main: '#ffcd00',
    },
    error: {
      main: '#D73737',
    },

    text: {
      primary: '#222222',
      secondary: '#464646',
    },

    /* **** Custom Colors **** */
    focusIndicator: '#3e5b00',

    tertiary: {
      dark: '#3e5b00',
      main: '#5a8200',
      light: '#7b9b33',
      contrastText: '#ffffff',
    },

    scoreTier: {
      1: '#005e3e',
      2: '#222222',
      3: '#9c4141',
    },
  },
});

function getQuantumClassOverrides(palette = quantumPalette) {
  const defaultClassOverrides = getDefaultClassOverrides(palette);

  // a darkened "green" to be used on typography/icon components to meet a 4.5:1 against the grey[100] background
  const accessibleDarkenedPrimaryColor = darken(palette.primary.main, 0.05);

  return {
    ...defaultClassOverrides,
    MuiTypography: {
      colorPrimary: {
        color: palette.secondary.main,
      },
      colorSecondary: {
        color: accessibleDarkenedPrimaryColor,
      },
    },
    MuiSvgIcon: {
      colorPrimary: {
        color: palette.secondary.main,
      },
      colorSecondary: {
        color: accessibleDarkenedPrimaryColor,
      },
    },
    MuiButton: {
      ...defaultClassOverrides.MuiButton,

      root: {
        ...defaultClassOverrides.MuiButton.root,
        '&.chat-button': {
          background: `linear-gradient(65deg, ${palette.secondary.main}, ${palette.primary.main} 20%)`,
          borderColor: palette.secondary.main,
        },
      },

      containedPrimary: {
        ...defaultClassOverrides.MuiButton.containedPrimary,
        '&$focusVisible': {
          outlineColor: lighten(palette.primary.main, 0.15),
          backgroundColor: darken(palette.primary.main, 0.3),
        },
      },

      containedSecondary: {
        ...defaultClassOverrides.MuiButton.containedSecondary,

        // there are some specific buttons for quantum that we want to override from containedSecondary to containedPrimary (accept cookies, and the result card details)
        // for any button in the app that is marked as secondary but needs to flip to primary, give it a className and add that className in the selector below
        '&.card-details-button, &.cookie-accept-button': {
          ...defaultClassOverrides.MuiButton.containedPrimary,
          backgroundColor: palette.primary.main,
          '&:hover': {
            backgroundColor: palette.primary.dark,
          },

          '&$focusVisible': {
            outlineColor: lighten(palette.primary.main, 0.15),
            backgroundColor: darken(palette.primary.main, 0.3),
          },
        },
      },

      /* Quantum never uses an outlined button with a green color - set this to mimic outlinedSecondary */
      outlinedPrimary: {
        ...defaultClassOverrides.MuiButton.outlinedSecondary,
        borderColor: palette.secondary.main,
        color: palette.secondary.main,
        '&:hover': {
          // this block was copied from https://github.com/mui/material-ui/blob/v4.12.3/packages/material-ui/src/Button/Button.js
          // from there it was modified to use the secondary color instead of primary
          border: `1px solid ${palette.secondary.main}`,
          backgroundColor: alpha(palette.secondary.main, palette.action.hoverOpacity),
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiAlert: {
      standardInfo: {
        '&.MarkdownNotification-root': { color: palette.secondary.main },
      },
      standardSuccess: {
        '&.MarkdownNotification-root': { color: palette.secondary.main },
      },
      standardWarning: {
        '&.MarkdownNotification-root': { color: palette.secondary.main },
      },
      standardError: {
        '&.MarkdownNotification-root': { color: palette.secondary.main },
      },
      outlinedInfo: {
        '&.MarkdownNotification-root': { color: palette.secondary.main },
      },
      outlinedSuccess: {
        '&.MarkdownNotification-root': { color: palette.secondary.main },
      },
      outlinedWarning: {
        '&.MarkdownNotification-root': { color: palette.secondary.main },
      },
      outlinedError: {
        '&.MarkdownNotification-root': { color: palette.secondary.main },
      },
    },
  };
}

const quantumThemeVariant = {
  palette: quantumPalette,
  typography: {},
  overrides: getQuantumClassOverrides(),
  props: {},
};

export default quantumThemeVariant;
